import { zodResolver } from "@hookform/resolvers/zod";
import { HardDrive } from "lucide-react";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useAuth } from "@/providers";
import { CustomFieldValuesSection } from "@/shared/components/CustomFields";
import { DeviceTypeSelect } from "@/shared/components/devices/DeviceTypeSelect";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useCreateDevice } from "@/shared/domain/devices/hooks/useCreateDevice";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  RobotoButton,
  TagInput,
  TextInput,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./CreateDevice.module.css";

const deviceFormSchema = z.object({
  // The device id is caller-chosen and must be unique within the org; the
  // backend rejects a collision, which surfaces in the error banner below.
  deviceId: z.string().trim().min(1, "Device ID is required"),
  tags: z.array(z.string()),
  deviceTypeId: z.string().nullable(),
});

type DeviceFormValues = z.infer<typeof deviceFormSchema>;

export interface CreateDeviceProps {
  close: () => void;
  /**
   * Device type preselected in the form, such as the type whose page opened
   * it.
   */
  deviceTypeId?: string;
}

export const CreateDevice: React.FC<CreateDeviceProps> = ({
  close,
  deviceTypeId = null,
}) => {
  const typeLabelId = React.useId();
  const { currentOrganization } = useAuth();
  const showNotification = useNotification();
  const { mutate: createDevice, isPending, error } = useCreateDevice();
  const assetStoreEnabled = useCheckFeatureFlag("release-asset-store");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<DeviceFormValues>({
    resolver: zodResolver(deviceFormSchema),
    defaultValues: {
      deviceId: "",
      tags: [],
      deviceTypeId,
    },
  });

  const [customFieldValues, setCustomFieldValues] = React.useState<
    Record<string, unknown>
  >({});

  const onSubmit = (values: DeviceFormValues) => {
    if (!currentOrganization?.org_id) {
      return;
    }

    createDevice(
      {
        orgId: currentOrganization.org_id,
        request: {
          device_id: values.deviceId.trim(),
          tags: values.tags.length ? values.tags : undefined,
          device_type_id: values.deviceTypeId ?? undefined,
          custom_fields:
            Object.keys(customFieldValues).length > 0
              ? customFieldValues
              : undefined,
        },
      },
      {
        onSuccess: (device) => {
          close();
          showNotification(`Device ${device.device_id} created`);
        },
      },
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.title}>
          <HardDrive size={14} /> New device
        </div>
      </div>

      <form
        className={styles.form}
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      >
        {error && (
          <div className={styles.errorBanner}>
            {userFacingErrorMessage(error)}
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-device-id">
            Device ID<span className={styles.req}>*</span>
          </label>
          <TextInput
            {...register("deviceId")}
            id="new-device-id"
            autoFocus
            autoComplete="off"
            placeholder="e.g. rover-01"
          />
          {errors.deviceId && (
            <span className={styles.fieldError}>{errors.deviceId.message}</span>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>Tags</label>
          <Controller
            control={control}
            name="tags"
            render={({ field }) => (
              <TagInput
                tags={field.value}
                onChange={field.onChange}
                aria-label="Tags"
              />
            )}
          />
        </div>

        {assetStoreEnabled && currentOrganization?.org_id && (
          <div className={styles.field}>
            <span className={styles.fieldLabel} id={typeLabelId}>
              Type
            </span>
            <Controller
              control={control}
              name="deviceTypeId"
              render={({ field }) => (
                <DeviceTypeSelect
                  orgId={currentOrganization.org_id}
                  value={field.value}
                  onChange={field.onChange}
                  aria-labelledby={typeLabelId}
                />
              )}
            />
          </div>
        )}

        {currentOrganization?.org_id && (
          <CustomFieldValuesSection
            entityType={TargetEntityType.Device}
            orgId={currentOrganization.org_id}
            values={customFieldValues}
            onChange={setCustomFieldValues}
            header={<label className={styles.fieldLabel}>Custom fields</label>}
          />
        )}

        <div className={styles.footer}>
          <RobotoButton type="button" variant="secondary" onClick={close}>
            Cancel
          </RobotoButton>
          <RobotoButton type="submit" variant="primary" disabled={isPending}>
            {isPending ? "Creating…" : "Create device"}
          </RobotoButton>
        </div>
      </form>
    </div>
  );
};
